import { Employee } from "../../../../contexts/interfaces";
import { useTranslation } from "react-i18next";
import classes from "./MainResultsTable.module.css";
import { FaCheckCircle, FaRegCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useRoutesContext } from "../../../../contexts/RoutesContext";
import { convertDate } from "../../../../utils/convertDate";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

interface Props {
  activePermits: Employee[];
  expiringPermits: Employee[];
  expiredPermits: Employee[];
  unfinishedForms: Employee[];
  waitingForms: Employee[];
  selectedFilter: string;
  searchEmployeeId: number | undefined;
  selectedUnitId: number;
}

const MainResultsTable = ({
  activePermits,
  expiringPermits,
  expiredPermits,
  unfinishedForms,
  waitingForms,
  selectedFilter,
  searchEmployeeId,
  selectedUnitId,
}: Props) => {
  const navigate = useNavigate();

  const { routes } = useRoutesContext();

  const { t } = useTranslation();

  const [resultsArray, setResultsArray] = useState<Employee[]>(activePermits);

  useEffect(() => {
    // Create holder array, which will hold data and can be filtered
    let holderArray: Employee[] = [];

    if (selectedFilter === "activePermits") {
      holderArray = [...activePermits];
    } else if (selectedFilter === "expiringPermits") {
      holderArray = [...expiringPermits];
    } else if (selectedFilter === "expiredPermits") {
      holderArray = [...expiredPermits];
    } else if (selectedFilter === "unfinishedForms") {
      holderArray = [...unfinishedForms];
    } else if (selectedFilter === "waitingForms") {
      holderArray = [...waitingForms];
    } else {
      holderArray = [...activePermits];
    }

    // If searchEmployeeId is set, filter array with the id of chosen employee
    if (searchEmployeeId) {
      holderArray = holderArray.filter(
        (item) => item.employeeId === searchEmployeeId
      );
    }

    // Sort array by date
    holderArray.sort(function (a: Employee, b: Employee) {
      return (
        (new Date(a.permitsValidUntil) as any) -
        (new Date(b.permitsValidUntil) as any)
      );
    });

    // Set holderArray as resultsArray
    setResultsArray([...holderArray]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, searchEmployeeId]);

  // TODO: Remove tooltip state and mouseEnter/mouseLeaves when package is updated to support react 18
  return (
    <>
      <Tooltip id="main-results-tooltip" float />
      <table className={classes.table}>
        <tbody>
          <tr>
            <th>{t("mainResultsTable.permitsValidUntil")}</th>
            <th>{t("mainResultsTable.name")}</th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineNaturallyLong")
              )}
            >
              {t("mainResultsTable.medicineNaturally")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicinePRNLong")
              )}
            >
              {t("mainResultsTable.medicinePRN")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineDistributionToDispenserLong")
              )}
            >
              {t("mainResultsTable.medicineDistributionToDispenser")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineOrderLong")
              )}
            >
              {t("mainResultsTable.medicineOrder")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineSCLong")
              )}
            >
              {t("mainResultsTable.medicineSC")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineIMLong")
              )}
            >
              {t("mainResultsTable.medicineIM")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineActualDrugsLong")
              )}
            >
              {t("mainResultsTable.medicineActualDrugs")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineSCCannulaLong")
              )}
            >
              {t("mainResultsTable.medicineSCCannula")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicinePKVLong")
              )}
            >
              {t("mainResultsTable.medicinePKV")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicinePKVDistributedLong")
              )}
            >
              {t("mainResultsTable.medicinePKVDistributed")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicineVaccinationLong")
              )}
            >
              {t("mainResultsTable.medicineVaccination")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicinePEGLong")
              )}
            >
              {t("mainResultsTable.medicinePEG")}
            </th>
            <th
              data-tooltip-id="main-results-tooltip"
              data-tooltip-content={String(
                t("mainResultsTable.medicinePOLong")
              )}
            >
              {t("mainResultsTable.medicinePO")}
            </th>
          </tr>
          {/* Go through every employee from resultsArray and render new row into the table */}
          {resultsArray && resultsArray.length > 0 ? (
            resultsArray.map((employee, i) => {
              return (
                <tr
                  key={i}
                  className={classes.tableEmployeeRow}
                  onClick={() =>
                    navigate(
                      `/${routes.profile}/${selectedUnitId}/${employee.employeeId}`
                    )
                  }
                >
                  <td>
                    {
                      // Show yellow dot, if employee is having expiring permit.
                      expiringPermits.find(
                        (emp) => emp.employeeId === employee.employeeId
                      ) ? (
                        <div className={classes.expirationDateRow}>
                          <div className={classes.expirationAlertDot} />
                          {convertDate(employee.permitsValidUntil)}
                        </div>
                      ) : (
                        // Otherwise just show the date
                        convertDate(employee.permitsValidUntil)
                      )
                    }
                  </td>
                  <td className={classes.employeeNameCell}>
                    <div className={classes.employeeName}>
                      {employee.firstName}, {employee.lastName}
                    </div>
                    {employee.jobTitle && (
                      <div className={classes.employeeJobTitle}>
                        {employee.jobTitle}
                      </div>
                    )}
                  </td>

                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineNatural"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicinePRN"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) =>
                        permit.type === "medicineDistributionToDispenser"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineOrder"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineSC"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineIM"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineActualDrugs"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineSCCannula"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicinePKV"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicinePKVDistributed"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicineVaccination"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicinePEG"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                  <td>
                    {employee.permits.findIndex(
                      (permit) => permit.type === "medicinePO"
                    ) !== -1 ? (
                      <FaCheckCircle className={classes.circleTrue} />
                    ) : (
                      <FaRegCircle className={classes.circleFalse} />
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td>
                <div className={classes.noResultsText}>
                  {String(t("mainResultsTable.noResultsText"))}
                  {searchEmployeeId &&
                    String(t("mainResultsTable.searchIsActive"))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

export default MainResultsTable;
